/**
 * 풀이 모드 — 레벨(백병동 『화성학』 편·장 누적).
 *
 * 원저: 제1편 초급(1~8장) · 제2편 고급(1~10장).
 * UI·생성기는 1편 2장~8장 · 2편 1장~9장 — 1편 1장(예비지식)·2편 10장은 제외.
 *
 * N편 M장을 고르면 그 장까지의 화음·기법이 누적 허용된다.
 * 화성 교체 단위는 Toolbar「중심 리듬」과 같다.
 */
import { BEAT_VALUES } from './musicTheory.js'

/**
 * @typedef {'triad'|'dom7'|'dim7'|'halfDim7'|'diatonic7'|'dom9'|'dom11'|'nct'|'modulation'|'secondary'|'secondaryDim7'|'modeMixture'|'neapolitan'|'aug6'|'augDom'|'otherAltered'|'advancedModulation'|'sequence'} SolveChordFamily
 */

/** @type {{ id: string, part: 1|2, chapter: number, label: string, title: string, unlocks: SolveChordFamily[] }[]} */
export const SOLVE_LEVELS = [
  // ── 제1편 초급 화성학 (제1장 예비지식은 풀이 레벨에서 제외) ──
  {
    id: '1-2',
    part: 1,
    chapter: 2,
    label: '1편 2장 · 3화음',
    title: '주3·부3·전위·종지까지 (온음계 3화음)',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad']),
  },
  {
    id: '1-3',
    part: 1,
    chapter: 3,
    label: '1편 3장 · 속7화음',
    title: '3화음 + 속7화음(V7) 및 전위',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad', 'dom7']),
  },
  {
    id: '1-4',
    part: 1,
    chapter: 4,
    label: '1편 4장 · 감7·반감7',
    title: '속7까지 + vii°7 / viiø7',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad', 'dom7', 'dim7', 'halfDim7']),
  },
  {
    id: '1-5',
    part: 1,
    chapter: 5,
    label: '1편 5장 · 부7화음',
    title: '감7까지 + 온음계 부7화음(ii7 등)',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7']),
  },
  {
    id: '1-6',
    part: 1,
    chapter: 6,
    label: '1편 6장 · 속9·속11',
    title: '부7까지 + V9 / V11',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11']),
  },
  {
    id: '1-7',
    part: 1,
    chapter: 7,
    label: '1편 7장 · 비화성음',
    title: '속9·11까지 + 경과·보조·계류 등 NCT',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct']),
  },
  {
    id: '1-8',
    part: 1,
    chapter: 8,
    label: '1편 8장 · 전조',
    title: '비화성음까지 + 온음계적·반음계적 전조',
    unlocks: /** @type {SolveChordFamily[]} */ (['triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation']),
  },
  // ── 제2편 고급 화성학 (10장 제외) ──
  {
    id: '2-1',
    part: 2,
    chapter: 1,
    label: '2편 1장 · 부속화음',
    title: '전조까지 + 부속화음(V/x, vii°/x)',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation', 'secondary',
    ]),
  },
  {
    id: '2-2',
    part: 2,
    chapter: 2,
    label: '2편 2장 · 부감7화음',
    title: '부속화음까지 + 부감7(vii°7/x 등)',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7',
    ]),
  },
  {
    id: '2-3',
    part: 2,
    chapter: 3,
    label: '2편 3장 · 변성화음',
    title: '부감7까지 + 모드 믹스처(변성화음)',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture',
    ]),
  },
  {
    id: '2-4',
    part: 2,
    chapter: 4,
    label: '2편 4장 · 나폴리6',
    title: '변성화음까지 + 나폴리 6화음(N6 / ♭II6)',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture', 'neapolitan',
    ]),
  },
  {
    id: '2-5',
    part: 2,
    chapter: 5,
    label: '2편 5장 · 증6화음',
    title: '나폴리까지 + It+6 / Fr+6 / Ger+6',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture', 'neapolitan', 'aug6',
    ]),
  },
  {
    id: '2-6',
    part: 2,
    chapter: 6,
    label: '2편 6장 · 증속화음',
    title: '증6까지 + 증속화음(augmented dominant)',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture', 'neapolitan', 'aug6', 'augDom',
    ]),
  },
  {
    id: '2-7',
    part: 2,
    chapter: 7,
    label: '2편 7장 · 그 밖의 변화화음',
    title: '증속까지 + 기타 변화화음',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture', 'neapolitan', 'aug6', 'augDom', 'otherAltered',
    ]),
  },
  {
    id: '2-8',
    part: 2,
    chapter: 8,
    label: '2편 8장 · 고급전조',
    title: '변화화음까지 + 고급 전조',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture', 'neapolitan', 'aug6', 'augDom', 'otherAltered',
      'advancedModulation',
    ]),
  },
  {
    id: '2-9',
    part: 2,
    chapter: 9,
    label: '2편 9장 · 동형·병진행',
    title: '고급전조까지 + 동형진행·병진행 (2편 10장 확장화음은 제외)',
    unlocks: /** @type {SolveChordFamily[]} */ ([
      'triad', 'dom7', 'dim7', 'halfDim7', 'diatonic7', 'dom9', 'dom11', 'nct', 'modulation',
      'secondary', 'secondaryDim7', 'modeMixture', 'neapolitan', 'aug6', 'augDom', 'otherAltered',
      'advancedModulation', 'sequence',
    ]),
  },
]

/** 기본: 1편 3장(속7화음) — 초급 연습에 가장 흔함 */
export const DEFAULT_SOLVE_LEVEL = '1-3'

export function getSolveLevel(levelId) {
  const id = normalizeSolveLevelId(levelId)
  return SOLVE_LEVELS.find((l) => l.id === id)
    ?? SOLVE_LEVELS.find((l) => l.id === DEFAULT_SOLVE_LEVEL)
}

/**
 * 예전 숫자 장 번호(2~9) → 새 id.
 * 9는 예전에 「부속화음=제2편1장」으로 매핑했음.
 */
function legacyChapterToId(n) {
  if (n >= 2 && n <= 8) return `1-${n}`
  if (n === 9) return '2-1'
  return null
}

/** @returns {string} */
export function normalizeSolveLevelId(levelId) {
  if (levelId == null || levelId === '') return DEFAULT_SOLVE_LEVEL
  if (typeof levelId === 'number' && Number.isFinite(levelId)) {
    return legacyChapterToId(levelId) ?? DEFAULT_SOLVE_LEVEL
  }
  const s = String(levelId).trim()
  if (/^\d+$/.test(s)) return legacyChapterToId(Number(s)) ?? DEFAULT_SOLVE_LEVEL
  if (s === '1-1') return DEFAULT_SOLVE_LEVEL // 예비지식은 레벨 메뉴에서 제외
  if (SOLVE_LEVELS.some((l) => l.id === s)) return s
  return DEFAULT_SOLVE_LEVEL
}

/** 해당 편·장까지 누적 허용된 화음/기법 집합 */
export function unlocksUpToLevel(levelId) {
  const lvl = getSolveLevel(levelId)
  return new Set(lvl?.unlocks ?? [])
}

/**
 * 해당 레벨에서 "새로" 등장하는 화음/기법(직전 레벨 대비 추가분) — 문제의 중심 소재.
 * 그 외 누적분(unlocksUpToLevel에서 이것만 뺀 나머지)은 배경으로만 섞여야 한다.
 */
export function focusFamiliesForLevel(levelId) {
  const id = normalizeSolveLevelId(levelId)
  const idx = SOLVE_LEVELS.findIndex((l) => l.id === id)
  if (idx <= 0) return new Set(SOLVE_LEVELS[0]?.unlocks ?? [])
  const prev = new Set(SOLVE_LEVELS[idx - 1].unlocks)
  return new Set(SOLVE_LEVELS[idx].unlocks.filter((f) => !prev.has(f)))
}

/** @deprecated unlocksUpToLevel 사용 */
export function unlocksUpToChapter(levelId) {
  return unlocksUpToLevel(levelId)
}

/**
 * 중심 리듬 음표 길이(4분음표 단위) = 화성 1회 유지 길이.
 * 예: 'w'→4, 'h'→2, 'q'→1, '8'→0.5
 */
export function harmonySpanBeats(baseRhythm = 'q') {
  const v = BEAT_VALUES[baseRhythm]
  return Number.isFinite(v) && v > 0 ? v : 1
}
